import React, { useState } from 'react';

/**
 * PickBreakdown - Displays detailed breakdown of winning bets
 * 
 * @param {Object} pick - The pick object containing payoutBreakdown
 * @param {Boolean} expanded - Whether to show expanded by default
 */
function PickBreakdown({ pick, expanded = false }) {
  const [isExpanded, setIsExpanded] = useState(expanded);

  if (!pick || !pick.payoutBreakdown || pick.actualPayout <= 0) {
    return null;
  }

  const { payoutBreakdown } = pick;
  const { details = [] } = payoutBreakdown;

  // Group details by bet type
  const winBets = details.filter(d => d.type === 'Win');
  const placeBets = details.filter(d => d.type === 'Place');
  const showBets = details.filter(d => d.type === 'Show');
  const exactaBets = details.filter(d => d.type === 'Exacta');
  const trifectaBets = details.filter(d => d.type === 'Trifecta');

  const getBetTypeEmoji = (type) => {
    switch (type) {
      case 'Win': return '🥇';
      case 'Place': return '🥈';
      case 'Show': return '🥉';
      case 'Exacta': return '🎯';
      case 'Trifecta': return '🎲';
      default: return '💰';
    }
  };

  const renderBetGroup = (betType, bets, totalAmount) => {
    if (bets.length === 0) return null;

    return (
      <div className="bet-group" style={{ marginBottom: '1rem' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.5rem',
          backgroundColor: '#f5f5f5',
          borderRadius: '4px',
          marginBottom: '0.5rem',
        }}>
          <span style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
            {getBetTypeEmoji(betType)} {betType}
          </span>
          <span style={{ color: '#4CAF50', fontWeight: 'bold' }}>
            ${totalAmount.toFixed(2)}
          </span>
        </div>
        <table style={{
          width: '100%',
          fontSize: '0.9rem',
          borderCollapse: 'collapse',
        }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #ddd' }}>
              <th style={{ textAlign: 'left', padding: '0.5rem' }}>Horse</th>
              {bets[0].position && <th style={{ textAlign: 'center', padding: '0.5rem' }}>Position</th>}
              <th style={{ textAlign: 'right', padding: '0.5rem' }}>Payout</th>
            </tr>
          </thead>
          <tbody>
            {bets.map((bet, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '0.5rem' }}>{bet.horse}</td>
                {bet.position && <td style={{ textAlign: 'center', padding: '0.5rem' }}>{bet.position}</td>}
                <td style={{ textAlign: 'right', padding: '0.5rem', color: '#4CAF50' }}>
                  ${bet.payout.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="pick-breakdown" style={{
      marginTop: '1rem',
      padding: '1rem',
      backgroundColor: '#f9f9f9',
      borderRadius: '8px',
      border: '1px solid #e0e0e0',
    }}>
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <h4 style={{ margin: 0, fontSize: '1rem' }}>
          💎 Winnings Breakdown
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ color: '#4CAF50', fontWeight: 'bold', fontSize: '1.1rem' }}>
            ${pick.actualPayout.toFixed(2)}
          </span>
          <span style={{ fontSize: '1.2rem' }}>
            {isExpanded ? '▼' : '▶'}
          </span>
        </div>
      </div>

      {isExpanded && (
        <div style={{ marginTop: '1rem' }}>
          {renderBetGroup('Win', winBets, payoutBreakdown.win || 0)}
          {renderBetGroup('Place', placeBets, payoutBreakdown.place || 0)}
          {renderBetGroup('Show', showBets, payoutBreakdown.show || 0)}
          {renderBetGroup('Exacta', exactaBets, payoutBreakdown.exacta || 0)}
          {renderBetGroup('Trifecta', trifectaBets, payoutBreakdown.trifecta || 0)}

          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            backgroundColor: '#e8f5e9',
            borderRadius: '4px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ fontWeight: 'bold' }}>Total Payout</span>
            <span style={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#2e7d32' }}>
              ${pick.actualPayout.toFixed(2)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default PickBreakdown;
